import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { getCourseById } from "../../data/courses";
import "./LinearAlgebraOverview.css";
import { getRequiredSections, getMinQuizScore } from "../../data/courseCompletion";
import { LA_MODULES, LA_EXPANSION_MODULES, getLaModuleParts, getLaModulePath, getLaModuleTopics, getLaTopicPath } from "../../data/laModules";

const CORE_CERT_PATHS = new Set([
  "/linear-algebra/vectors/1",
  "/linear-algebra/matrices/1",
  "/linear-algebra/systems/1",
  "/linear-algebra/eigen/1",
  "/linear-algebra/orthogonality/1",
  "/linear-algebra/svd/1",
  ...[...LA_MODULES, ...LA_EXPANSION_MODULES].map((module) => getLaModulePath(module)),
]);

const EXCLUDED_FROM_ROADMAP = new Set([
  "/linear-algebra/overview",
  "/practice",
  "/quiz/linear-algebra",
]);

function LinearAlgebraOverview() {
  const { hash } = useLocation();
  useEffect(() => {
    if ([...LA_MODULES, ...LA_EXPANSION_MODULES].some((module) => hash === `#${module.overviewAnchor}`)) {
      document.getElementById(hash.slice(1))?.scrollIntoView();
    }
  }, [hash]);
  const course = getCourseById("linear-algebra");
  const roadmap = (course?.modules || []).filter((m) => !EXCLUDED_FROM_ROADMAP.has(m.path));
  const firstTopicPath = roadmap[0]?.path || "/linear-algebra/linear-equations/1";

  return (
    <main className="home-page la-overview">
      <section className="home-hero" style={{ minHeight: "auto" }}>
        <div className="hero-copy">
          <p className="eyebrow">Start here · Course overview</p>
          <h1>Linear Algebra, from first principles</h1>
          <p>
            Linear algebra is the mathematics of lines, planes, and the flat spaces they live in — and
            it quietly powers everything from computer graphics and machine learning to the calculus
            you may already know. This page is your map: what you'll learn, in what order, and how the
            course is put together, before you dive into the first module.
          </p>
          <div className="hero-actions">
            <Link className="secondary-action" to="/courses/linear-algebra">
              ← All modules
            </Link>
            <Link className="primary-action" to={firstTopicPath}>
              Begin with {roadmap[0]?.title || "Linear Equations"} →
            </Link>
          </div>
        </div>
        <div className="hero-image-container course-hero-image-container">
          <img
            src="/images/courses/linear-algebra-hero.svg"
            alt="Linear Algebra Vectors and Matrix Transformation Visual"
            className="hero-graph-img course-hero-img"
          />
        </div>
      </section>

      <section className="guide-section" aria-labelledby="why-heading">
        <div className="section-kicker">Why it matters</div>
        <h2 id="why-heading">What linear algebra actually gives you</h2>
        <p className="la-overview-lead">
          Every topic in this course is one idea, applied over and over: keep everything straight —
          lines, planes, transformations — and track it with coordinates instead of pictures once the
          picture gets too big to draw. That single trick is what lets a computer represent a 3D scene,
          a search engine rank web pages, or a neural network process an image.
        </p>
        <ul className="la-overview-bullets">
          <li>Solve systems of equations that show up in engineering, economics, and physics.</li>
          <li>Represent rotations, scaling, and 3D graphics as matrices you can multiply.</li>
          <li>Understand the linear algebra underneath machine learning, statistics, and data science.</li>
        </ul>
      </section>

      <section className="guide-section" aria-labelledby="roadmap-heading">
        <div className="section-kicker">Roadmap</div>
        <h2 id="roadmap-heading">What you'll learn, in order</h2>
        <ol className="la-roadmap">
          {roadmap.map((mod, i) => {
            const isCore = CORE_CERT_PATHS.has(mod.path);
            return (
              <li key={mod.path}>
                <Link to={mod.path} className="la-roadmap-row">
                  <span className="la-roadmap-num">{i + 1}</span>
                  <span className="la-roadmap-copy">
                    <span className="la-roadmap-title">
                      {mod.title}
                      <span className={`la-roadmap-tag${isCore ? " la-roadmap-tag--core" : ""}`}>
                        {isCore ? "Required for certificate" : LA_EXPANSION_MODULES.some((module) => getLaModulePath(module) === mod.path) ? "New topics" : "Extra depth"}
                      </span>
                    </span>
                    <small>{mod.description}</small>
                  </span>
                  <span className="la-roadmap-arrow" aria-hidden="true">→</span>
                </Link>
              </li>
            );
          })}
        </ol>
      </section>

      {LA_MODULES.map((module) => (
        <section className="guide-section" id={module.overviewAnchor} key={module.id} aria-labelledby={`${module.id}-heading`}>
          <div className="section-kicker">Linear Algebra · 4 topics · 80 checkpoint questions</div>
          <h2 id={`${module.id}-heading`}>{module.title}</h2>
          <p className="la-overview-lead">{module.description}</p>
          <p>Two parts, with two complete topics in each. Every topic ends with its own 20-question checkpoint. Score at least 80% on both checkpoints to complete a part.</p>
          {[1, 2].map((part) => (
            <div key={part}>
              <h3><Link to={getLaModulePath(module, part)}>Part {part} — Topics {part * 2 - 1} and {part * 2}</Link></h3>
              <ol className="la-roadmap">
                {getLaModuleTopics(module, part).map((topic) => (
                  <li key={topic.id}>
                    <Link className="la-roadmap-row" to={getLaTopicPath(module, topic)}>
                      <span className="la-roadmap-num">{module.topics.indexOf(topic) + 1}</span>
                      <span className="la-roadmap-copy">
                        <span className="la-roadmap-title">{topic.title}</span>
                        <small>Theory, worked examples, and a 20-question checkpoint</small>
                      </span>
                      <span className="la-roadmap-arrow" aria-hidden="true">→</span>
                    </Link>
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </section>
      ))}

      {LA_EXPANSION_MODULES.map((module) => (
        <section className="guide-section" id={module.overviewAnchor} key={module.id} aria-labelledby={`${module.id}-heading`}>
          <h2 id={`${module.id}-heading`}>{module.title}</h2>
          <p>{module.description}</p>
          {getLaModuleParts(module).map((part) => (
            <p key={part}><Link to={getLaModulePath(module, part)}>Part {part} — {getLaModuleTopics(module, part).map((topic) => topic.title).join(" · ")}</Link></p>
          ))}
          <p>Each part includes one complete topic with theory, worked examples, and 20 checkpoint questions. Score at least 80% on its checkpoint to complete that part.</p>
          {module.plannedNextTopic && <p>Part 2 planned: {module.plannedNextTopic}.</p>}
        </section>
      ))}

      <section className="guide-section" aria-labelledby="structure-heading">
        <div className="section-kicker">How this course works</div>
        <h2 id="structure-heading">Structure &amp; certificate</h2>
        <p className="la-overview-lead">
          The three four-topic advanced modules each have two parts and four topics. Core topic guides
          retain their existing theory, examples, and quizzes. Score 80%+ on a section's quizzes to unlock "Mark as
          complete" — your progress and quiz scores are saved to your account automatically.
        </p>
        <p className="la-overview-lead">
          Complete all {getRequiredSections("linear-algebra").length} required parts: both parts of <strong>Vectors &amp; Vector Spaces</strong>,{" "}
          <strong>Matrices &amp; Determinants</strong>, <strong>Systems of Linear Equations</strong>,{" "}
          <strong>Eigenvalues &amp; Eigenvectors</strong>, <strong>Orthogonality &amp; Least Squares</strong>,{" "}
          <strong>Singular Value Decomposition</strong>, the three four-topic advanced modules, and the three two-topic expansion modules.
          Then pass the certification quiz with a score of at least {getMinQuizScore("linear-algebra")}%.
          <strong> Linear Equations</strong> and <strong>Linear Transformations</strong> remain
          optional additional study.
        </p>
      </section>

      <section className="guide-section" aria-labelledby="prereq-heading">
        <div className="section-kicker">Before you start</div>
        <h2 id="prereq-heading">Prerequisites</h2>
        <p className="la-overview-lead">
          Comfort with basic algebra is all you need — solving an equation for a variable, and plotting
          a point on the x–y plane. No calculus is required to begin; the first module, Linear
          Equations, starts from that exact algebra and builds up from there.
        </p>
      </section>
    </main>
  );
}

export default LinearAlgebraOverview;
